import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { Link } from "react-router-dom";
import { Award, Flame, TrendingDown, TrendingUp } from "lucide-react";

function Ring({ value, size = 70, stroke = 7 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, value)) / 100) * circ;
  const color = value >= 75 ? "#00FF41" : value >= 40 ? "#00E5FF" : "#F59E0B";
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#27272A" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function Progress() {
  const [data, setData] = useState(null);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: labsData }] = await Promise.all([
          api.get("/progress"),
          api.get("/labs"),
        ]);
        setData(p);
        setLabs(labsData.labs || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byLab = Object.fromEntries(labs.map((l) => [l.id, l]));

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <main className="max-w-[1800px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-1">
            // progress
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Your trajectory.</h1>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono" data-testid="progress-loading">loading…</div>
        ) : (
          <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-[#141416] border border-zinc-800 p-5 flex items-center gap-4">
                <Ring value={data.readiness_score} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
                    readiness
                  </div>
                  <div className="font-term text-3xl font-bold text-[#00E5FF]">{data.readiness_score}</div>
                </div>
              </div>
              <div className="bg-[#141416] border border-zinc-800 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                  <Flame size={12} className="text-[#F59E0B]" /> streak
                </div>
                <div className="font-term text-3xl font-bold mt-2 text-[#F59E0B]">{data.streak_days}d</div>
              </div>
              <div className="bg-[#141416] border border-zinc-800 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                  <Award size={12} className="text-[#00FF41]" /> completed
                </div>
                <div className="font-term text-3xl font-bold mt-2 text-[#00FF41]">
                  {data.completed_labs}/{data.total_labs}
                </div>
              </div>
              <div className="bg-[#141416] border border-zinc-800 p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                  <TrendingUp size={12} className="text-[#00E5FF]" /> this week
                </div>
                <div className="font-term text-3xl font-bold mt-2 text-[#00E5FF]">{data.labs_this_week}</div>
              </div>
            </div>

            {/* Domains breakdown */}
            <div className="bg-[#141416] border border-zinc-800 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-4">
                domains
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.domains.map((d) => (
                  <div key={d.code} className="flex items-center gap-4 p-4 bg-black border border-zinc-800" data-testid={`progress-domain-${d.code}`}>
                    <Ring value={d.percentage} size={56} stroke={6} />
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm truncate">{d.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">
                        {d.completed}/{d.total} labs · avg {d.avg_score}
                      </div>
                      <div className="text-[10px] text-zinc-600 font-mono">blueprint weight {d.weight}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Weak areas */}
            <div className="bg-[#141416] border border-zinc-800 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F59E0B] font-mono mb-3 flex items-center gap-2">
                <TrendingDown size={12} /> focus areas
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {data.weak_areas.map((w) => (
                  <div key={w.code} className="p-4 bg-black border border-zinc-800" data-testid={`weak-${w.code}`}>
                    <div className="text-xs text-zinc-400 font-mono">{w.name}</div>
                    <div className="font-term text-2xl font-bold text-[#F59E0B] mt-1">{w.percentage}%</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent activity */}
            <div className="bg-[#141416] border border-zinc-800 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-4">
                activity
              </div>
              {data.progress.length === 0 ? (
                <div className="text-sm text-zinc-500">
                  No lab attempts yet. <Link to="/labs" className="text-[#00E5FF] hover:underline">start a lab</Link>.
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {data.progress
                    .sort((a, b) => (b.last_attempt || "").localeCompare(a.last_attempt || ""))
                    .slice(0, 10)
                    .map((p) => {
                      const lab = byLab[p.lab_id];
                      if (!lab) return null;
                      return (
                        <Link
                          key={p.lab_id}
                          to={`/labs/${p.lab_id}`}
                          className="py-3 flex items-center justify-between hover:bg-zinc-900/50 transition-colors px-2"
                          data-testid={`activity-${p.lab_id}`}
                        >
                          <div>
                            <div className="font-display font-semibold">{lab.title}</div>
                            <div className="text-xs text-zinc-500 font-mono">
                              {lab.topic} · {p.attempts} attempts · status {p.status}
                            </div>
                          </div>
                          <div className={`font-term text-2xl font-bold ${p.best_score === 100 ? "text-[#00FF41]" : p.best_score >= 60 ? "text-[#00E5FF]" : "text-[#F59E0B]"}`}>
                            {p.best_score}
                          </div>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
